import { useEffect, useState, useCallback } from "react";
import { Flame, Users, RefreshCw, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/**
 * Inline 30-day sparkline rendered as SVG. Each bar = 1 day routings count.
 */
function Sparkline({ data, width = 120, height = 24, testId }) {
  if (!data || data.length === 0) {
    return <div className="text-[10px] text-gray-600">no data</div>;
  }
  const max = Math.max(...data, 1);
  const barWidth = width / data.length;
  return (
    <svg
      width={width} height={height}
      data-testid={testId}
      className="overflow-visible"
    >
      {data.map((v, i) => {
        const h = Math.max(1, (v / max) * height);
        return (
          <rect
            key={`bar-${i}`}
            x={i * barWidth + 0.5}
            y={height - h}
            width={Math.max(1, barWidth - 1)}
            height={h}
            fill={v > 0 ? "#fb923c" : "#27272a"}
            rx={1}
          />
        );
      })}
    </svg>
  );
}

/**
 * Routing Activity widget — last 5 hot-lead auto-routings on the Manager Dashboard.
 *
 * Surfaces fairness signals: who got the last 5 hot leads, with what score, and
 * how the round-robin is distributing. Closers can ask "am I being skipped?"
 * and managers can answer with data instead of guesswork.
 */
export function RoutingActivityWidget() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ days: [], reps: {} });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRows, rStats] = await Promise.all([
        fetch(`${API}/audit/routing-activity?limit=5`, { credentials: "include" }),
        fetch(`${API}/audit/routing-stats?days=30`, { credentials: "include" }),
      ]);
      if (rRows.ok) {
        const data = await rRows.json();
        setRows(Array.isArray(data) ? data : []);
      } else {
        setRows([]);
      }
      if (rStats.ok) {
        const data = await rStats.json();
        setStats(data || { days: [], reps: {} });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Top-routed reps over 30 days (sorted by total).
  const repTotals = Object.entries(stats.reps || {}).map(([name, series]) => ({
    name, total: series.reduce((a, b) => a + b, 0), series,
  })).sort((a, b) => b.total - a.total).slice(0, 5);

  return (
    <div
      data-testid="routing-activity-widget"
      className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-400" />
          <h3 className="text-white font-semibold">Routing Activity</h3>
          <span className="text-xs text-gray-500">last 5 + 30-day trend</span>
        </div>
        <Button
          variant="ghost" size="sm"
          onClick={fetchAll}
          disabled={loading}
          className="text-gray-400 hover:text-white"
          data-testid="routing-activity-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center" data-testid="routing-activity-empty">
          {loading ? "Loading…" : "No hot-lead routings yet. Once a lead crosses into the Hot tier, it'll appear here."}
        </div>
      ) : (
        <>
          <div className="space-y-2 mb-4">
            {rows.map((e) => (
              <div
                key={e.event_id}
                data-testid={`routing-row-${e.event_id}`}
                className="flex items-center justify-between rounded-md bg-white/[0.03] border border-white/5 px-3 py-2 text-xs"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Flame className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                  <span className="text-white truncate" title={e.target_name}>
                    {e.target_name}
                  </span>
                  <span className="text-gray-500 shrink-0">→</span>
                  <span className="text-emerald-300 truncate font-medium">
                    {e.details?.reassigned_to_name || "Unknown"}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-500 shrink-0 ml-3">
                  <span className="font-mono">{e.details?.score ?? "?"}/100</span>
                  <span>{formatRelative(e.created_at)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* 30-day sparklines per rep */}
          {repTotals.length > 0 && (
            <div className="pt-3 border-t border-white/5">
              <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
                <BarChart3 className="w-3.5 h-3.5" />
                <span>30-day trend</span>
              </div>
              <div className="space-y-1.5">
                {repTotals.map((r) => (
                  <div key={r.name} className="flex items-center gap-3 text-xs">
                    <span className="text-gray-300 w-32 truncate" title={r.name}>{r.name}</span>
                    <Sparkline
                      data={r.series}
                      testId={`routing-sparkline-${r.name.replace(/\s+/g, '-')}`}
                    />
                    <span className="text-gray-400 font-mono w-8 text-right">{r.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top-5 distribution bar over the latest 5 routings */}
          <div className="pt-3 mt-3 border-t border-white/5">
            <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
              <Users className="w-3.5 h-3.5" />
              <span>Last 5 distribution</span>
            </div>
            <div className="space-y-1.5">
              {Object.entries(rows.reduce((acc, e) => {
                const name = e.details?.reassigned_to_name || "Unknown";
                acc[name] = (acc[name] || 0) + 1;
                return acc;
              }, {})).map(([name, count]) => (
                <div key={name} className="flex items-center gap-2 text-xs">
                  <span className="text-gray-300 w-32 truncate" title={name}>{name}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-amber-400"
                      style={{ width: `${(count / rows.length) * 100}%` }}
                      data-testid={`routing-bar-${name.replace(/\s+/g, '-')}`}
                    />
                  </div>
                  <span className="text-gray-400 font-mono w-8 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
